/**
 * Propagates group box changes into descendant shapes in **canvas-absolute** coordinates (same
 * space as checkpoint/API child `base` fields). Do not convert children into group-local storage.
 */
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  boxVerticesForShapeGeometry,
  normalizeRotationDegrees,
  rotateVector,
  shortestDeltaDegrees,
} from '@/lib/transforms/geometry'
import { type ShapeTransformSnapshot, isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { shouldSkipPropagatedGroupTransformOnShape } from '@/lib/groups/element-edit-policy'
import type { ApplyShapePartialUpdate } from './types/apply-shape-partial-update'
import type { RotatedRect } from './types/rotated-rect'

const GROUP_TRANSFORM_MIN_DIMENSION = 0.01

/** Maps a rotated rectangle through the same group move/resize/rotate as descendant shapes. */
export function mapRotatedRectThroughGroupGeometryChange(
  rect: RotatedRect,
  beforeGroup: ShapeData,
  afterGroup: ShapeData
): RotatedRect {
  const beforeCenter = {
    x: beforeGroup.x + (beforeGroup.width ?? 0) / 2,
    y: beforeGroup.y + (beforeGroup.height ?? 0) / 2,
  }
  const afterCenter = {
    x: afterGroup.x + (afterGroup.width ?? 0) / 2,
    y: afterGroup.y + (afterGroup.height ?? 0) / 2,
  }
  const beforeRotation = beforeGroup.rotation ?? 0
  const afterRotation = afterGroup.rotation ?? 0
  const rotationDelta = shortestDeltaDegrees(beforeRotation, afterRotation)
  const beforeWidth = Math.max(GROUP_TRANSFORM_MIN_DIMENSION, Math.abs(beforeGroup.width ?? 0))
  const beforeHeight = Math.max(GROUP_TRANSFORM_MIN_DIMENSION, Math.abs(beforeGroup.height ?? 0))
  const afterWidth = Math.max(GROUP_TRANSFORM_MIN_DIMENSION, Math.abs(afterGroup.width ?? 0))
  const afterHeight = Math.max(GROUP_TRANSFORM_MIN_DIMENSION, Math.abs(afterGroup.height ?? 0))
  const sx = afterWidth / beforeWidth
  const sy = afterHeight / beforeHeight

  const width = rect.width ?? 0
  const height = rect.height ?? 0
  const nextRotation = normalizeRotationDegrees((rect.rotation ?? 0) + rotationDelta)
  const transformedVertices = boxVerticesForShapeGeometry({
    x: rect.x,
    y: rect.y,
    width,
    height,
    rotation: rect.rotation ?? 0,
  }).map((vertex) => {
    const fromBeforeCenter = {
      x: vertex.x - beforeCenter.x,
      y: vertex.y - beforeCenter.y,
    }
    const localBefore = rotateVector(fromBeforeCenter, -beforeRotation)
    const localAfter = {
      x: localBefore.x * sx,
      y: localBefore.y * sy,
    }
    const worldAfterOffset = rotateVector(localAfter, afterRotation)

    return {
      x: afterCenter.x + worldAfterOffset.x,
      y: afterCenter.y + worldAfterOffset.y,
    }
  })
  let minProjectedX = Infinity
  let maxProjectedX = -Infinity
  let minProjectedY = Infinity
  let maxProjectedY = -Infinity

  for (const vertex of transformedVertices) {
    const projected = rotateVector(vertex, -nextRotation)

    minProjectedX = Math.min(minProjectedX, projected.x)
    maxProjectedX = Math.max(maxProjectedX, projected.x)
    minProjectedY = Math.min(minProjectedY, projected.y)
    maxProjectedY = Math.max(maxProjectedY, projected.y)
  }

  const nextWidth = Math.max(GROUP_TRANSFORM_MIN_DIMENSION, maxProjectedX - minProjectedX)
  const nextHeight = Math.max(GROUP_TRANSFORM_MIN_DIMENSION, maxProjectedY - minProjectedY)
  const projectedCenter = {
    x: (minProjectedX + maxProjectedX) / 2,
    y: (minProjectedY + maxProjectedY) / 2,
  }
  const nextCenter = rotateVector(projectedCenter, nextRotation)

  return {
    x: nextCenter.x - nextWidth / 2,
    y: nextCenter.y - nextHeight / 2,
    width: nextWidth,
    height: nextHeight,
    rotation: nextRotation,
  }
}

/**
 * Maps a flat set of descendant snapshots (captured once, e.g. at gesture start) through a
 * single group before/after transform. Unlike repeatedly calling `updateShape` frame-by-frame
 * with the *previous* frame's group box as `beforeGroup`, this always transforms from the same
 * fixed baseline — so `GROUP_TRANSFORM_MIN_DIMENSION` flooring on a shrunk descendant in one
 * frame can never poison the ratio math of later frames (see live group resize in
 * `shape-manager/gestures/group-resize.ts`).
 */
export function mapGroupTransformInitialsToAbsoluteUpdates(
  initials: Record<string, ShapeTransformSnapshot>,
  beforeGroup: ShapeData,
  afterGroup: ShapeData
): Record<string, RotatedRect> {
  const out: Record<string, RotatedRect> = {}

  for (const [id, rect] of Object.entries(initials)) {
    out[id] = mapRotatedRectThroughGroupGeometryChange(rect, beforeGroup, afterGroup)
  }

  return out
}

export function applyGroupTransformToDescendants(
  list: ShapeData[],
  groupId: string,
  beforeGroup: ShapeData,
  afterGroup: ShapeData,
  applyPartialUpdate: ApplyShapePartialUpdate
): ShapeData[] {
  const byId = new Map(list.map((shape) => [shape.id, shape]))
  const descendants = list.filter(
    (shape) => shape.id !== groupId && isDescendantOfGroup(shape, groupId, byId)
  )

  if (descendants.length === 0) return list

  let nextList = list

  for (const shape of descendants) {
    if (shouldSkipPropagatedGroupTransformOnShape(shape)) {
      continue
    }

    const next = mapRotatedRectThroughGroupGeometryChange(
      {
        x: shape.x,
        y: shape.y,
        width: shape.width ?? 0,
        height: shape.height ?? 0,
        rotation: shape.rotation ?? 0,
      },
      beforeGroup,
      afterGroup
    )

    nextList = applyPartialUpdate(nextList, shape.id, {
      x: next.x,
      y: next.y,
      width: next.width,
      height: next.height,
      rotation: next.rotation,
    })
  }

  return nextList
}
